import React, { useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';
import type { SeasonalIntroPhase } from '../../hooks/useSeasonalPalette';

/**
 * The christmas "curtain", sibling of HalloweenIntro: a cool translucent overlay, a frost
 * crystal drawing itself into two corners and a gust of heavy snow crossing while
 * useSeasonalPalette switches the palette underneath. Pointer-transparent, so it never
 * blocks the lobby. Transform, opacity and stroke-dashoffset only.
 */

interface ChristmasIntroProps {
  phase: Exclude<SeasonalIntroPhase, 'idle'>;
}

const CRYSTAL_SIZE = 400;
const CRYSTAL_ARMS = 6;
const GUST_FLAKES = 14;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const drawIn = keyframes`
  from { stroke-dashoffset: 1; }
  to { stroke-dashoffset: 0; }
`;

const gust = keyframes`
  from { transform: translate3d(0, -12vh, 0); }
  to { transform: translate3d(var(--gust-drift), 112vh, 0); }
`;

const Overlay = styled.div.attrs({ className: 'christmas-intro' })<{ $phase: ChristmasIntroProps['phase'] }>`
  position: fixed;
  inset: 0;
  z-index: 10001;
  pointer-events: none;
  overflow: hidden;
  background: radial-gradient(120% 90% at 50% 40%, rgba(10, 18, 28, 0.18), rgba(10, 18, 28, 0.7));
  /* Keyframes picked per phase must go through css\`\` so styled-components injects them. */
  ${(props) =>
    props.$phase === 'out'
      ? css`
          animation: ${fadeOut} 0.9s ease forwards;
        `
      : css`
          animation: ${fadeIn} 0.7s ease forwards;
        `}
`;

// Tucked into the corner with a fifth of it off-screen, so most of the crystal shows and it
// still reads as growing in from the edge, the way the halloween web does.
const Crystal = styled.svg<{ $corner: 'top-left' | 'bottom-right' }>`
  position: absolute;
  width: 46vmin;
  height: 46vmin;
  ${(props) =>
    props.$corner === 'top-left'
      ? 'top: -9vmin; left: -9vmin;'
      : 'bottom: -9vmin; right: -9vmin; transform: rotate(30deg);'}
  fill: none;
  stroke: rgba(191, 224, 255, 0.7);
  stroke-width: 1.6;
  stroke-linecap: round;

  path {
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
    animation: ${drawIn} 1.5s ease-out forwards;
  }

  path[data-branches] {
    animation-delay: 0.4s;
  }
`;

const Flake = styled.div<{ $left: number; $size: number; $duration: number; $delay: number; $drift: number }>`
  position: absolute;
  top: 0;
  left: ${(props) => props.$left}%;
  width: ${(props) => props.$size}px;
  height: ${(props) => props.$size}px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  --gust-drift: ${(props) => props.$drift}px;
  animation: ${gust} ${(props) => props.$duration}s linear forwards;
  animation-delay: ${(props) => props.$delay}s;
  will-change: transform;
`;

const fmt = (n: number): string => n.toFixed(1);

// Branch pairs per arm as [position along the arm, length], both as fractions of the arm.
const BRANCHES: Array<[number, number]> = [
  [0.3, 0.3],
  [0.52, 0.24],
  [0.72, 0.17],
  [0.9, 0.1],
];
const BRANCH_SPREAD = Math.PI / 3;

// A six-armed crystal: main arms, a small hexagon at the heart and fern-like branch pairs
// leaning outward along each arm, all drawn from the centre.
const buildCrystal = (): { arms: string; branches: string } => {
  const half = CRYSTAL_SIZE / 2;
  const arm = half * 0.96;
  const angles = Array.from({ length: CRYSTAL_ARMS }, (_, i) => (Math.PI * 2 * i) / CRYSTAL_ARMS);
  const point = (angle: number, radius: number, offsetAngle = angle, offset = 0): string =>
    `${fmt(half + radius * Math.cos(angle) + offset * Math.cos(offsetAngle))} ${fmt(half + radius * Math.sin(angle) + offset * Math.sin(offsetAngle))}`;

  const heart = angles.map((a, i) => `${i === 0 ? 'M' : 'L'}${point(a + Math.PI / 6, arm * 0.12)}`).join(' ');
  const arms = `${angles.map((a) => `M${point(a, 0)} L${point(a, arm)}`).join(' ')} ${heart} Z`;

  const branches = angles
    .map((a) =>
      BRANCHES.map(([along, length]) => {
        const base = point(a, arm * along);
        const left = point(a, arm * along, a - BRANCH_SPREAD, arm * length);
        const right = point(a, arm * along, a + BRANCH_SPREAD, arm * length);
        return `M${base} L${left} M${base} L${right}`;
      }).join(' '),
    )
    .join(' ');

  return { arms, branches };
};

const GUST = Array.from({ length: GUST_FLAKES }, (_, i) => ({
  id: i,
  left: (i * 37) % 100,
  size: 4 + ((i * 5) % 6),
  duration: 1.6 + ((i * 3) % 5) * 0.2,
  delay: 0.2 + ((i * 7) % 9) * 0.12,
  drift: 40 + ((i * 13) % 7) * 12,
}));

const ChristmasIntro: React.FC<ChristmasIntroProps> = ({ phase }) => {
  const crystal = useMemo(buildCrystal, []);
  const viewBox = `0 0 ${CRYSTAL_SIZE} ${CRYSTAL_SIZE}`;

  return (
    <Overlay $phase={phase} aria-hidden="true">
      <Crystal $corner="top-left" viewBox={viewBox}>
        <path d={crystal.arms} pathLength={1} />
        <path d={crystal.branches} pathLength={1} data-branches="" />
      </Crystal>
      <Crystal $corner="bottom-right" viewBox={viewBox}>
        <path d={crystal.arms} pathLength={1} />
        <path d={crystal.branches} pathLength={1} data-branches="" />
      </Crystal>
      {GUST.map((flake) => (
        <Flake
          key={flake.id}
          $left={flake.left}
          $size={flake.size}
          $duration={flake.duration}
          $delay={flake.delay}
          $drift={flake.drift}
        />
      ))}
    </Overlay>
  );
};

export default ChristmasIntro;
